import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import type {
  Relationship,
  RelationshipState,
  RelationshipsBySourceEntry,
} from './relationshipTypes';
import {
  fetchRelationships,
  createBidirectionalRelationship,
  deleteBidirectionalRelationship,
  setRelationshipPrimary,
  setRelationshipPinned,
  updateRelationshipDescriptions,
} from './relationshipThunks';

const initialState: RelationshipState = {
  bySource: {},
};

/**
 * Chiave di indicizzazione per sorgente. `className` e' il FQN Java (come
 * arriva dal backend e come lo passano i thunk), non il typeName corto:
 * costruirla da un typeName produce un bucket che nessuno popolera' mai.
 */
export function relationshipSourceKey(className: string, classPK: number): string {
  return `${className}:${classPK}`;
}

/**
 * Raggruppa le relazioni per FQN del target (className2).
 * I consumatori (RelationPanel, AddRelationDrawer) convertono il loro
 * targetType short in FQN via getEntityFqn prima di indicizzare.
 */
function groupByTargetType(items: Relationship[]): Record<string, Relationship[]> {
  const groups: Record<string, Relationship[]> = {};
  for (const rel of items) {
    const key = rel.className2;
    if (!groups[key]) groups[key] = [];
    groups[key].push(rel);
  }
  return groups;
}

function emptyEntry(): RelationshipsBySourceEntry {
  return { items: [], byTargetType: {}, status: 'idle', error: null };
}

/** Bucket della sorgente, creandolo se assente. Per i reducer che scrivono. */
function ensureEntry(state: RelationshipState, key: string): RelationshipsBySourceEntry {
  if (!state.bySource[key]) state.bySource[key] = emptyEntry();
  return state.bySource[key];
}

/**
 * Applica `fn` a ogni bucket che contiene la relazione `relationshipId`, e
 * riallinea il suo indice per target.
 *
 * Serve perche' una stessa relazione puo' vivere in piu' bucket: la fetch e'
 * bidirezionale (`getByEntityBidirectional`), quindi la rel Person->Business
 * compare sia nel bucket di Person sia in quello di Business, normalizzata nei
 * due versi. Aggiornare solo il bucket "di partenza" lascerebbe l'altro con un
 * record stantio finche' non si rimonta la pagina.
 */
function updateInAllSources(
  state: RelationshipState,
  relationshipId: number,
  fn: (rel: Relationship, entry: RelationshipsBySourceEntry) => void,
): void {
  for (const entry of Object.values(state.bySource)) {
    const rel = entry.items.find((r) => r.relationshipId === relationshipId);
    if (!rel) continue;
    fn(rel, entry);
    entry.byTargetType = groupByTargetType(entry.items);
  }
}

export const relationshipSlice = createSlice({
  name: 'relationships',
  initialState,
  reducers: {
    /**
     * Svuota le relazioni di UNA sorgente (cleanup di unmount di una detail).
     * Mirata per costruzione: prima azzerava lo slice intero, il che smontando
     * una pagina invalidava anche gli scope di eventuali altri pannelli.
     */
    clearRelationships(state, action: PayloadAction<{ className: string; classPK: number }>) {
      delete state.bySource[relationshipSourceKey(action.payload.className, action.payload.classPK)];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRelationships.pending, (state, action) => {
        const entry = ensureEntry(
          state,
          relationshipSourceKey(action.meta.arg.className, action.meta.arg.classPK),
        );
        entry.status = 'loading';
        entry.error = null;
      })
      .addCase(fetchRelationships.fulfilled, (state, action) => {
        const entry = ensureEntry(
          state,
          relationshipSourceKey(action.meta.arg.className, action.meta.arg.classPK),
        );
        entry.status = 'succeeded';
        entry.items = action.payload;
        entry.byTargetType = groupByTargetType(action.payload);
      })
      .addCase(fetchRelationships.rejected, (state, action) => {
        const entry = ensureEntry(
          state,
          relationshipSourceKey(action.meta.arg.className, action.meta.arg.classPK),
        );
        entry.status = 'failed';
        entry.error = action.payload as string;
      })
      .addCase(createBidirectionalRelationship.fulfilled, (state, action) => {
        // Aggiungi il record forward (il primo) — il reverse e' per l'altra
        // entità, ma estraiamo la sua `description` e la salviamo come
        // `reverseDescription` sul direct: così il rendering del
        // RelationPanel (che mostra reverseDescription || description) vede
        // subito il "ruolo dell'altro verso me" senza dover aspettare un
        // refetch. Coerente con `getByEntityBidirectional` che già fa il
        // pairing direct↔inverse via partner key.
        const [direct, inverse] = action.payload;
        // Il bucket è quello del lato 1 del record creato, che NON coincide
        // sempre con la pagina aperta: QuotationForm crea Person->Quotation
        // mentre si sta guardando un Quotation. Se quel bucket non è mai stato
        // fetchato lo si lascia assente invece di crearne uno parziale: un
        // bucket a metà è indistinguibile da uno completo per chi legge, e la
        // prima fetch di quella sorgente lo popolerebbe comunque per intero.
        const key = relationshipSourceKey(direct.className1, direct.classPK1);
        const entry = state.bySource[key];
        if (!entry) return;
        entry.items.push({
          ...direct,
          reverseDescription: inverse?.description ?? null,
          reverseRelationshipId: inverse?.relationshipId ?? null,
        });
        entry.byTargetType = groupByTargetType(entry.items);
      })
      .addCase(deleteBidirectionalRelationship.fulfilled, (state, action) => {
        // Gli id cancellati sono globali (forward + reverse) e la coppia può
        // essere presente in due bucket: si potano tutti.
        const deletedIds = new Set(action.payload);
        for (const entry of Object.values(state.bySource)) {
          const before = entry.items.length;
          entry.items = entry.items.filter((r) => !deletedIds.has(r.relationshipId));
          if (entry.items.length !== before) entry.byTargetType = groupByTargetType(entry.items);
        }
      })
      .addCase(setRelationshipPrimary.fulfilled, (state, action) => {
        const updated = action.payload;
        // 1) Allinea il record col backend (è la verità: primary + modifiedDate
        //    riflettono lo stato post-patch) in ogni bucket che lo contiene.
        //    NB: preserva reverseDescription locale che il backend non conosce.
        updateInAllSources(state, updated.relationshipId, (rel) => {
          Object.assign(rel, updated, { reverseDescription: rel.reverseDescription });
        });
        // 2) Se il record è stato promosso a primary, demota localmente gli altri
        //    della stessa coppia (className1, classPK1, className2) per riflettere
        //    l'invariante "max 1" applicata dal backend (_demoteOthers). Su
        //    primary=false (toggle off) la coppia resta semplicemente senza
        //    principale e nessun altro record va toccato.
        //    Il demote è confinato al bucket della sorgente: className1/classPK1
        //    arrivano dalla response, non dall'argomento del dispatch (che per
        //    setPinned non li ha affatto).
        if (updated.primary) {
          const entry = state.bySource[relationshipSourceKey(updated.className1, updated.classPK1)];
          if (entry) {
            for (const r of entry.items) {
              if (
                r.relationshipId !== updated.relationshipId &&
                r.className1 === updated.className1 &&
                r.classPK1 === updated.classPK1 &&
                r.className2 === updated.className2 &&
                r.primary
              ) {
                r.primary = false;
              }
            }
            entry.byTargetType = groupByTargetType(entry.items);
          }
        }
      })
      .addCase(updateRelationshipDescriptions.fulfilled, (state, action) => {
        // Ogni descrizione salvata vive in due posti: come `description` del suo
        // record e come `reverseDescription` del record gemello, nel bucket
        // dell'altra entità. Si aggiornano entrambi, in tutti i bucket.
        // L'indice per target va ricostruito dove si è toccato qualcosa, come
        // negli altri case: punta agli stessi record di `items`.
        const saved = [action.payload.direct, action.payload.reverse]
          .filter((r): r is Relationship => r !== null);
        for (const entry of Object.values(state.bySource)) {
          let touched = false;
          for (const r of entry.items) {
            for (const rel of saved) {
              if (r.relationshipId === rel.relationshipId) {
                r.description = rel.description;
                touched = true;
              }
              if (r.reverseRelationshipId === rel.relationshipId) {
                r.reverseDescription = rel.description;
                touched = true;
              }
            }
          }
          if (touched) entry.byTargetType = groupByTargetType(entry.items);
        }
      })
      .addCase(setRelationshipPinned.fulfilled, (state, action) => {
        const updated = action.payload;
        // 1) Allinea il record col backend (pinned + modifiedDate). Preserva
        //    reverseDescription locale che il backend non conosce.
        updateInAllSources(state, updated.relationshipId, (rel) => {
          Object.assign(rel, updated, { reverseDescription: rel.reverseDescription });
        });
        // 2) Se promosso a pinned, demota localmente gli altri della stessa
        //    SORGENTE (className1, classPK1) — il vincolo pinned NON include
        //    className2 (a differenza di primary): al più 1 pinnata per sorgente.
        //    Che è esattamente il bucket: il demote coincide col suo perimetro.
        if (updated.pinned) {
          const entry = state.bySource[relationshipSourceKey(updated.className1, updated.classPK1)];
          if (entry) {
            for (const r of entry.items) {
              if (
                r.relationshipId !== updated.relationshipId &&
                r.className1 === updated.className1 &&
                r.classPK1 === updated.classPK1 &&
                r.pinned
              ) {
                r.pinned = false;
              }
            }
            entry.byTargetType = groupByTargetType(entry.items);
          }
        }
      });
  },
});

// ── Selector ────────────────────────────────────────────────────────────────
// Costanti condivise, MAI letterali inline: un `?? []` dentro un selector
// restituisce un array nuovo a ogni chiamata, e con useSelector (uguaglianza
// referenziale) questo significa re-render a ciclo infinito.
const EMPTY_ITEMS: Relationship[] = [];
const EMPTY_BY_TARGET: Record<string, Relationship[]> = {};

/** Relazioni della sorgente indicata. `className` deve essere un FQN. */
export function selectRelationshipsBySource(className: string | undefined, classPK: number) {
  return (state: RootState): Relationship[] =>
    (className
      ? state.relationships.bySource[relationshipSourceKey(className, classPK)]?.items
      : undefined) ?? EMPTY_ITEMS;
}

/** Relazioni della sorgente, indicizzate per FQN del target. */
export function selectRelationshipsByTargetType(className: string | undefined, classPK: number) {
  return (state: RootState): Record<string, Relationship[]> =>
    (className
      ? state.relationships.bySource[relationshipSourceKey(className, classPK)]?.byTargetType
      : undefined) ?? EMPTY_BY_TARGET;
}

/** Stato di caricamento della sola sorgente indicata. */
export function selectRelationshipStatusBySource(className: string | undefined, classPK: number) {
  return (state: RootState): RelationshipsBySourceEntry['status'] =>
    (className
      ? state.relationships.bySource[relationshipSourceKey(className, classPK)]?.status
      : undefined) ?? 'idle';
}

export const { clearRelationships } = relationshipSlice.actions;
export const relationshipReducer = relationshipSlice.reducer;
